import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ArrowUp, Paperclip, Smile, X } from 'lucide-react';
import EmojiPicker from '../parts/EmojiPicker';
import { fileToneClass } from '../Messages/MessageAttachments';
import { describeAttachment, extensionOf, formatFileSize } from '../utils/chatFormat';
import styles from '../Chat.module.css';

const MAX_BYTES = 100 * 1024 * 1024;
// Unsent text per conversation, for this tab.
const drafts = new Map();

let localSeq = 0;
const nextLocalId = () => { localSeq += 1; return `att-${Date.now()}-${localSeq}`; };

/** Clipboard images arrive as "image.png" — name them like a screenshot. */
const nameForPasted = (file) => {
  if (file.name && file.name !== 'image.png' && file.name !== 'image.jpeg') return file.name;
  const d = new Date();
  const date = d.toISOString().slice(0, 10);
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).replace(':', '.');
  const ext = (file.type || 'image/png').split('/')[1] || 'png';
  return `Screenshot ${date} at ${time}.${ext === 'jpeg' ? 'jpg' : ext}`;
};

/**
 * Files upload the moment they're attached (pasted, dropped or picked), so
 * Send only posts the message. `onSend({ text, attachments })` hands the
 * message to StreamChatBox, which shows it as Sending… until Stream echoes it.
 * The ref exposes addFiles(files) for the conversation-wide drop zone.
 */
const CustomMessageInput = forwardRef(({ channel, conversationName, onSend }, ref) => {
  const [message, setMessage] = useState(() => drafts.get(channel?.cid) || '');
  const [showEmoji, setShowEmoji] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;

  // Typing indicators are driven by real keystrokes only. The SDK throttles
  // typing.start to one per 2s and its client cleaner sends typing.stop ~1s
  // after the last keystroke, so no local timer is needed.
  const sendTyping = (fn) => {
    if (!channel) return;
    channel[fn]().catch(() => {});
  };

  useEffect(() => () => {
    if (channel?.isTyping) channel.stopTyping().catch(() => {});
  }, [channel]);

  // Release object URLs on unmount.
  useEffect(() => () => {
    attachmentsRef.current.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl));
  }, []);

  const resize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  };

  useEffect(() => { resize(); }, []);

  const upload = async (item) => {
    try {
      const isImage = item.kind === 'image';
      const response = isImage
        ? await channel.sendImage(item.file, item.name, item.file.type)
        : await channel.sendFile(item.file, item.name, item.file.type);
      setAttachments((prev) => prev.map((a) => (a.localId === item.localId
        ? { ...a, status: 'done', url: response.file, thumbUrl: response.thumb_url }
        : a)));
    } catch (err) {
      console.error('[CustomMessageInput] Upload failed:', err);
      setAttachments((prev) => prev.map((a) => (a.localId === item.localId
        ? { ...a, status: 'error', error: err?.response?.data?.message || 'Upload failed' }
        : a)));
    }
  };

  const addFiles = (fileList, { pasted = false } = {}) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length || !channel) return;
    const items = files.map((file) => {
      const name = pasted ? nameForPasted(file) : file.name;
      const kind = String(file.type).startsWith('image/') && !/svg/.test(file.type) ? 'image' : 'file';
      const tooBig = file.size > MAX_BYTES;
      return {
        localId: nextLocalId(),
        file,
        name,
        kind,
        size: file.size,
        mime: file.type,
        previewUrl: kind === 'image' ? URL.createObjectURL(file) : null,
        status: tooBig ? 'error' : 'uploading',
        error: tooBig ? 'Over the 100 MB limit' : '',
      };
    });
    setAttachments((prev) => [...prev, ...items]);
    items.filter((i) => i.status === 'uploading').forEach(upload);
    textareaRef.current?.focus();
  };

  useImperativeHandle(ref, () => ({ addFiles }));

  const removeAttachment = (localId) => {
    setAttachments((prev) => {
      const gone = prev.find((a) => a.localId === localId);
      if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl);
      return prev.filter((a) => a.localId !== localId);
    });
  };

  const ready = attachments.filter((a) => a.status === 'done');
  const uploading = attachments.some((a) => a.status === 'uploading');
  const canSend = !uploading && (message.trim().length > 0 || ready.length > 0);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!canSend || !channel) return;
    const payload = {
      text: message.trim(),
      attachments: ready.map((a) => (a.kind === 'image'
        ? {
          type: 'image', title: a.name, image_url: a.url, asset_url: a.url, thumb_url: a.thumbUrl,
          mime_type: a.mime, file_size: a.size,
        }
        : { type: 'file', title: a.name, asset_url: a.url, mime_type: a.mime, file_size: a.size })),
    };
    onSend?.(payload);
    attachments.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl));
    setMessage('');
    drafts.delete(channel.cid);
    setAttachments((prev) => prev.filter((a) => a.status === 'error'));
    sendTyping('stopTyping');
    requestAnimationFrame(resize);
  };

  const handleInputChange = (e) => {
    setMessage(e.target.value);
    drafts.set(channel?.cid, e.target.value);
    if (e.target.value) sendTyping('keystroke');
    else if (channel?.isTyping) sendTyping('stopTyping');
    resize();
  };

  const handlePaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (!files.length) return;
    // Text + image on the clipboard (e.g. copied from a doc): keep the text too.
    if (!e.clipboardData.getData('text/plain')) e.preventDefault();
    addFiles(files, { pasted: true });
  };

  const insertEmoji = (char) => {
    const el = textareaRef.current;
    const at = el?.selectionStart ?? message.length;
    const next = message.slice(0, at) + char + message.slice(el?.selectionEnd ?? at);
    setMessage(next);
    drafts.set(channel?.cid, next);
    setShowEmoji(false);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(at + char.length, at + char.length);
    });
  };

  return (
    <div className={styles.inputArea}>
      <form onSubmit={handleSubmit} className={styles.composer}>
        {attachments.length > 0 && (
          <div className={styles.tray}>
            {attachments.map((a) => {
              if (a.kind === 'image' && a.status !== 'error') {
                return (
                  <div key={a.localId} className={styles.trayThumb} title={a.name}>
                    <img src={a.previewUrl} alt={a.name} />
                    {a.status === 'uploading' && (
                      <span className={`${styles.progress} ${styles.indeterminate}`} aria-label="Uploading"><span /></span>
                    )}
                    <button type="button" className={styles.trayRemove} onClick={() => removeAttachment(a.localId)} aria-label={`Remove ${a.name}`}>
                      <X size={10} strokeWidth={3} />
                    </button>
                  </div>
                );
              }
              const d = describeAttachment({ title: a.name, mime_type: a.mime, type: 'file', asset_url: 'local' });
              return (
                <div key={a.localId} className={`${styles.trayFile} ${a.status === 'error' ? styles.error : ''}`}>
                  <span className={`${styles.fileIcon} ${styles.fileIconSm} ${fileToneClass(d.tone)}`} aria-hidden="true">
                    {(extensionOf(a.name, a.mime) || 'file').toUpperCase().slice(0, 4)}
                  </span>
                  <span className={styles.trayFileText}>
                    <span className={styles.trayFileName} title={a.name}>{a.name}</span>
                    {a.status === 'uploading' ? (
                      <span className={`${styles.progress} ${styles.indeterminate}`} aria-label="Uploading"><span /></span>
                    ) : (
                      <span className={styles.trayFileMeta}>
                        {a.status === 'error' ? a.error : [d.typeLabel, formatFileSize(a.size)].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </span>
                  <button type="button" className={styles.trayRemove} onClick={() => removeAttachment(a.localId)} aria-label={`Remove ${a.name}`}>
                    <X size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <label style={{ display: 'block' }}>
          <span className={styles.srOnly}>Message {conversationName}</span>
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleInputChange}
            onPaste={handlePaste}
            placeholder={conversationName ? `Message ${conversationName}` : 'Write a message'}
            className={styles.textarea}
            rows={1}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
        </label>

        <div className={styles.composerBar}>
          <button type="button" onClick={() => fileInputRef.current?.click()} className={styles.iconBtn} aria-label="Attach files" title="Attach files">
            <Paperclip size={17} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
            style={{ display: 'none' }}
          />
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setShowEmoji((v) => !v)}
              className={`${styles.iconBtn} ${showEmoji ? styles.active : ''}`}
              aria-label="Add emoji"
              aria-expanded={showEmoji}
            >
              <Smile size={17} />
            </button>
            {showEmoji && (
              <EmojiPicker onPick={insertEmoji} onClose={() => setShowEmoji(false)} style={{ bottom: 'calc(100% + 8px)', left: 0 }} />
            )}
          </div>
          <span className={styles.composerHint}>
            {uploading ? 'Uploading…' : 'Enter to send · Shift+Enter for a new line · Paste screenshots'}
          </span>
          <button
            type="submit"
            disabled={!canSend}
            className={`${styles.sendBtn} ${canSend ? styles.ready : ''}`}
            aria-label="Send"
          >
            <ArrowUp size={17} strokeWidth={2.2} />
          </button>
        </div>
      </form>
    </div>
  );
});

CustomMessageInput.displayName = 'CustomMessageInput';

export default CustomMessageInput;
